import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Box, FormInputText, InputLabel, PaginatedSearchDropdown, SimpleLabel } from '@hopskipdrive/react-components';
import { InvoiceGroup } from '@rideiq/organizers/features/InvoiceGroups/types';
import { useInvoiceGroups } from '@rideiq/organizers/hooks/useInvoiceGroups';
import { variables } from '@rideiq/shared/components/variables';
import { debounce } from 'lodash';

export type InvoiceGroupOption = { value: string; label: string; isDefault?: boolean };

interface Props {
  onChange: (option: InvoiceGroupOption) => void;
  // Called instead of onChange for the on-load default selection, so it doesn't dirty the form.
  onAutoSelect?: (option: InvoiceGroupOption) => void;
  initialValue?: InvoiceGroup | null;
  selectDefault?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const id = 'invoice-groups-filter';
const PER_PAGE = 20;

export const InvoiceGroupDropdown = ({
  initialValue,
  selectDefault,
  onChange,
  onAutoSelect,
  error,
  helperText,
  required = false,
}: Props) => {
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState<string>('');
  const [currentValue, setCurrentValue] = useState<string>(initialValue?.name || '');
  const searchQueryRef = useRef('');
  const { invoiceGroups, isFetching, fetchInvoiceGroups, addMissingInvoiceGroup } = useInvoiceGroups();

  const options: InvoiceGroupOption[] = invoiceGroups.map((group) => ({
    value: String(group.id),
    label: group.name,
    isDefault: group.isDefault,
  }));

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        searchQueryRef.current = query;
        setPage(1);
        fetchInvoiceGroups({ page: 1, perPage: PER_PAGE, ...(query && { q: query }) });
      }, 300),
    [fetchInvoiceGroups]
  );

  const handleNextPage = () => {
    const nextPage = page + 1;

    setPage(nextPage);
    fetchInvoiceGroups({
      page: nextPage,
      perPage: PER_PAGE,
      ...(searchQueryRef.current && { q: searchQueryRef.current }),
    });
  };

  const selectedValue = useMemo(() => inputValue || initialValue?.name, [inputValue, initialValue]);

  useEffect(() => {
    if (initialValue) {
      addMissingInvoiceGroup(initialValue);

      if (currentValue !== initialValue.name && inputValue) {
        setInputValue('');
        setCurrentValue(initialValue.name);
      }
    }
  }, [initialValue]);

  useEffect(() => {
    fetchInvoiceGroups({ page: 1, perPage: PER_PAGE });
  }, []);

  useEffect(() => {
    if (!selectDefault || currentValue) {
      return;
    }
    const defaultGroup = invoiceGroups?.find((g) => g.isDefault);

    if (defaultGroup) {
      const option: InvoiceGroupOption = { value: String(defaultGroup.id), label: defaultGroup.name, isDefault: true };

      (onAutoSelect ?? onChange)(option);
      setInputValue(defaultGroup.name);
      setCurrentValue(defaultGroup.name);
    }
  }, [invoiceGroups]);

  return (
    <PaginatedSearchDropdown
      openOnFocus
      id={id}
      data-testid={id}
      options={options}
      isLoading={isFetching}
      inputValue={inputValue}
      setInputValue={setInputValue}
      onInputChange={(_, value, reason) => {
        const newValue = value || currentValue;

        setInputValue(newValue);

        if (reason !== 'input') {
          debouncedSearch.cancel();

          return;
        }

        debouncedSearch(newValue);
      }}
      perPage={PER_PAGE}
      pageNumber={page}
      loadNextPage={handleNextPage}
      autoHighlight
      {...(selectedValue && { value: selectedValue })}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      renderOption={(props, option) => (
        <li {...props}>
          <Box display="flex" alignItems="center" gap="8px">
            {option.label}
            {option.isDefault && (
              <SimpleLabel
                label="Default"
                bgColor="neutral95"
                style={{ color: variables.colorPalette.neutralVariant60 }}
              />
            )}
          </Box>
        </li>
      )}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      renderInput={(params) => (
        <FormInputText
          {...params}
          variant="outlined"
          placeholder="Invoice Group"
          inputProps={{ ...params.inputProps, 'aria-required': required }}
          error={error}
          helperText={helperText}
        />
      )}
      onChange={(_, value) => {
        if (!value || typeof value === 'string') {
          return;
        }
        onChange(value);
        const label = typeof value === 'string' ? value : value.label;
        const hadSearchQuery = Boolean(searchQueryRef.current);

        searchQueryRef.current = '';
        setCurrentValue(label);

        if (hadSearchQuery) {
          setPage(1);
          fetchInvoiceGroups({ page: 1, perPage: PER_PAGE });
        }
      }}
      slotProps={{
        clearIndicator: {
          sx: {
            visibility: 'hidden !important',
          },
        },
        paper: {
          sx: {
            maxHeight: '200px',
            overflow: 'hidden',
          },
        },
      }}
    />
  );
};

interface InvoiceGroupFormFieldProps {
  control: Control<RiderFormType>;
  error: boolean;
  helperText: string;
  initialValue?: InvoiceGroup;
  selectDefault?: boolean;
  onAutoSelect?: (option: InvoiceGroupOption) => void;
}

export const InvoiceGroupFormField: FC<InvoiceGroupFormFieldProps> = ({
  control,
  error,
  helperText,
  initialValue = undefined,
  selectDefault = false,
  onAutoSelect,
}) => {
  return (
    <>
      <InputLabel fieldtype="required" htmlFor={id}>
        Invoice group
      </InputLabel>
      <Controller
        control={control}
        name="invoiceGroupId"
        render={({ field: { onChange } }) => (
          <InvoiceGroupDropdown
            required
            initialValue={initialValue}
            selectDefault={selectDefault}
            onChange={(option) => onChange(option?.value ?? null)}
            onAutoSelect={onAutoSelect}
            error={error}
            helperText={helperText}
          />
        )}
      />
    </>
  );
};
